import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';
import { fetchWithCSRF } from '../../utils/apiUtils';

// Fetch today tasks for a board
export const fetchTodayTasks = createAsyncThunk(
    'todayTask/fetchTodayTasks',
    async ({ boardId, date }, { rejectWithValue }) => {
        try {
            const dateParam = date || new Date().toISOString().split('T')[0];
            const response = await fetchWithCSRF(
                `${config.API_URI}/v1/today-tasks?boardId=${boardId}&date=${dateParam}`,
                { credentials: 'include' }
            );

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                return rejectWithValue(err.message || 'Failed to fetch today tasks');
            }

            const data = await response.json();
            return data.tasks;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Add a card to My Day
export const addTodayTask = createAsyncThunk(
    'todayTask/addTodayTask',
    async ({ cardId, boardId, date }, { rejectWithValue }) => {
        try {
            const response = await fetchWithCSRF(
                `${config.API_URI}/v1/today-tasks`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ cardId, boardId, date })
                }
            );

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                return rejectWithValue(err.message || 'Failed to add to My Day');
            }

            const data = await response.json();
            return data.task;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Remove a card from My Day
export const removeTodayTask = createAsyncThunk(
    'todayTask/removeTodayTask',
    async (taskId, { rejectWithValue }) => {
        try {
            const response = await fetchWithCSRF(
                `${config.API_URI}/v1/today-tasks/${taskId}`,
                {
                    method: 'DELETE',
                    credentials: 'include'
                }
            );

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                return rejectWithValue(err.message || 'Failed to remove from My Day');
            }

            return taskId;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Toggle task completion
export const toggleTodayTask = createAsyncThunk(
    'todayTask/toggleTodayTask',
    async (taskId, { rejectWithValue }) => {
        try {
            const response = await fetchWithCSRF(
                `${config.API_URI}/v1/today-tasks/${taskId}/toggle`,
                {
                    method: 'PATCH',
                    credentials: 'include'
                }
            );

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                return rejectWithValue(err.message || 'Failed to toggle task');
            }

            const data = await response.json();
            return data.task;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Reorder tasks
export const reorderTodayTasks = createAsyncThunk(
    'todayTask/reorderTodayTasks',
    async (orderedIds, { rejectWithValue }) => {
        try {
            const response = await fetchWithCSRF(
                `${config.API_URI}/v1/today-tasks/reorder`,
                {
                    method: 'PUT',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ orderedIds })
                }
            );

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                return rejectWithValue(err.message || 'Failed to reorder tasks');
            }

            return orderedIds;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Carry over yesterday's incomplete tasks
export const carryOverTasks = createAsyncThunk(
    'todayTask/carryOverTasks',
    async ({ boardId }, { rejectWithValue }) => {
        try {
            const response = await fetchWithCSRF(
                `${config.API_URI}/v1/today-tasks/carry-over`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ boardId })
                }
            );

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                return rejectWithValue(err.message || 'Failed to carry over tasks');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Update time estimate for a task
export const updateEstimate = createAsyncThunk(
    'todayTask/updateEstimate',
    async ({ taskId, estimate }, { rejectWithValue }) => {
        try {
            const response = await fetchWithCSRF(
                `${config.API_URI}/v1/today-tasks/${taskId}/estimate`,
                {
                    method: 'PATCH',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ estimate })
                }
            );

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                return rejectWithValue(err.message || 'Failed to update estimate');
            }

            const data = await response.json();
            return data.task;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Get yesterday's incomplete count
export const fetchYesterdayCount = createAsyncThunk(
    'todayTask/fetchYesterdayCount',
    async ({ boardId }, { rejectWithValue }) => {
        try {
            const response = await fetchWithCSRF(
                `${config.API_URI}/v1/today-tasks/yesterday-count?boardId=${boardId}`,
                { credentials: 'include' }
            );

            if (!response.ok) return rejectWithValue('Failed');

            const data = await response.json();
            return data.count;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Update personal note
export const updateNote = createAsyncThunk(
    'todayTask/updateNote',
    async ({ taskId, note }, { rejectWithValue }) => {
        try {
            const response = await fetchWithCSRF(
                `${config.API_URI}/v1/today-tasks/${taskId}/note`,
                {
                    method: 'PATCH',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ note })
                }
            );
            if (!response.ok) return rejectWithValue('Failed to update note');
            const data = await response.json();
            return data.task;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Fetch suggested cards
export const fetchSuggestions = createAsyncThunk(
    'todayTask/fetchSuggestions',
    async ({ boardId }, { rejectWithValue }) => {
        try {
            const response = await fetchWithCSRF(
                `${config.API_URI}/v1/today-tasks/suggestions?boardId=${boardId}`,
                { credentials: 'include' }
            );
            if (!response.ok) return rejectWithValue('Failed to fetch suggestions');
            const data = await response.json();
            return data.suggestions;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const todayTaskSlice = createSlice({
    name: 'todayTask',
    initialState: {
        tasks: [],
        loading: false,
        error: null,
        cardIds: [],
        yesterdayCount: 0,
        suggestions: [],
        suggestionsLoading: false
    },
    reducers: {
        setTodayTasks: (state, action) => {
            state.tasks = action.payload;
            state.cardIds = action.payload.map(t => t.card?._id || t.card);
        },
        // Optimistic reorder
        reorderTasksLocal: (state, action) => {
            state.tasks = action.payload;
        },
        // Sync completion from board card
        syncCardCompletion: (state, action) => {
            const { shortId, completed } = action.payload;
            const task = state.tasks.find(t => t.card?.shortId === shortId);
            if (task) {
                task.completed = completed;
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch
            .addCase(fetchTodayTasks.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTodayTasks.fulfilled, (state, action) => {
                state.loading = false;
                state.tasks = action.payload;
                state.cardIds = action.payload.map(t => t.card?._id || t.card);
            })
            .addCase(fetchTodayTasks.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Add
            .addCase(addTodayTask.fulfilled, (state, action) => {
                state.tasks.push(action.payload);
                const cardId = action.payload.card?._id || action.payload.card;
                if (!state.cardIds.includes(cardId)) {
                    state.cardIds.push(cardId);
                }
            })
            // Remove
            .addCase(removeTodayTask.fulfilled, (state, action) => {
                const removedTask = state.tasks.find(t => t._id === action.payload);
                state.tasks = state.tasks.filter(t => t._id !== action.payload);
                if (removedTask) {
                    const cardId = removedTask.card?._id || removedTask.card;
                    state.cardIds = state.cardIds.filter(id => id !== cardId);
                }
            })
            // Toggle
            .addCase(toggleTodayTask.fulfilled, (state, action) => {
                const task = state.tasks.find(t => t._id === action.payload._id);
                if (task) {
                    task.completed = action.payload.completed;
                }
            })
            // Reorder
            .addCase(reorderTodayTasks.fulfilled, (state, action) => {
                // Already handled optimistically via reorderTasksLocal
            })
            // Carry over
            .addCase(carryOverTasks.fulfilled, (state, action) => {
                const newTasks = action.payload.tasks || [];
                for (const task of newTasks) {
                    if (!state.tasks.find(t => t._id === task._id)) {
                        state.tasks.push(task);
                        const cardId = task.card?._id || task.card;
                        if (!state.cardIds.includes(cardId)) {
                            state.cardIds.push(cardId);
                        }
                    }
                }
                state.yesterdayCount = 0;
            })
            // Estimate
            .addCase(updateEstimate.fulfilled, (state, action) => {
                const task = state.tasks.find(t => t._id === action.payload._id);
                if (task) {
                    task.estimate = action.payload.estimate;
                }
            })
            // Yesterday count
            .addCase(fetchYesterdayCount.fulfilled, (state, action) => {
                state.yesterdayCount = action.payload;
            })
            // Note
            .addCase(updateNote.fulfilled, (state, action) => {
                const task = state.tasks.find(t => t._id === action.payload._id);
                if (task) {
                    task.note = action.payload.note;
                }
            })
            // Suggestions
            .addCase(fetchSuggestions.pending, (state) => {
                state.suggestionsLoading = true;
            })
            .addCase(fetchSuggestions.fulfilled, (state, action) => {
                state.suggestions = action.payload;
                state.suggestionsLoading = false;
            })
            .addCase(fetchSuggestions.rejected, (state) => {
                state.suggestionsLoading = false;
            });
    }
});

export const {
    setTodayTasks,
    reorderTasksLocal,
    syncCardCompletion
} = todayTaskSlice.actions;

export default todayTaskSlice.reducer;
